import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { safePercent } from "../components/layout/splitGeometry";

let preferencesFailed = false;
const memory = new Map<string, string>();
function reportFailure() {
  if (preferencesFailed) return;
  preferencesFailed = true;
  queueMicrotask(() => useUI.setState({ preferenceError: true }));
}
const safeStorage = {
  getItem(name: string) {
    try {
      return localStorage.getItem(name);
    } catch {
      reportFailure();
      return memory.get(name) ?? null;
    }
  },
  setItem(name: string, value: string) {
    memory.set(name, value);
    try {
      localStorage.setItem(name, value);
    } catch {
      reportFailure();
    }
  },
  removeItem(name: string) {
    memory.delete(name);
    try {
      localStorage.removeItem(name);
    } catch {
      reportFailure();
    }
  },
};

interface UIState {
  preferenceError: boolean;
  theme: "light" | "dark" | "system";
  sidebarWidth: number;
  editorPercent: number;
  readingPercent: number;
  setTheme: (theme: UIState["theme"]) => void;
  setLayout: (sidebarWidth: number, editorPercent: number) => void;
  setReadingPercent: (readingPercent: number) => void;
}
export const useUI = create<UIState>()(
  persist(
    (set) => ({
      preferenceError: false,
      theme: "light",
      sidebarWidth: 232,
      editorPercent: 52,
      readingPercent: 50,
      setReadingPercent: (readingPercent) =>
        set({ readingPercent: safePercent(readingPercent, 50) }),
      setTheme: (theme) => set({ theme }),
      setLayout: (sidebarWidth, editorPercent) =>
        set({
          sidebarWidth: Number.isFinite(sidebarWidth)
            ? Math.min(320, Math.max(200, sidebarWidth))
            : 232,
          editorPercent: safePercent(editorPercent, 52),
        }),
    }),
    {
      name: "fls-ui:v1",
      version: 1,
      storage: createJSONStorage(() => safeStorage),
      partialize: ({ theme, sidebarWidth, editorPercent, readingPercent }) => ({
        theme,
        sidebarWidth,
        editorPercent,
        readingPercent,
      }),
      merge: (stored, current) => {
        const value =
          stored && typeof stored === "object"
            ? (stored as Partial<UIState>)
            : {};
        return {
          ...current,
          theme: ["light", "dark", "system"].includes(String(value.theme))
            ? value.theme!
            : "light",
          sidebarWidth:
            typeof value.sidebarWidth === "number" &&
            Number.isFinite(value.sidebarWidth)
              ? Math.max(200, Math.min(320, value.sidebarWidth))
              : 232,
          editorPercent:
            typeof value.editorPercent === "number" &&
            Number.isFinite(value.editorPercent)
              ? safePercent(value.editorPercent, 52)
              : 52,
          readingPercent:
            typeof value.readingPercent === "number"
              ? safePercent(value.readingPercent, 50)
              : 50,
        };
      },
    },
  ),
);
